/* ==========================================================================
   Prios - Customer Form
   - Client-side validation (no digits in names/city, email format, required)
   - International phone input (intl-tel-input) with country selector
   - Google Places address autocomplete (fills address / zip / city / country)
   - Submits the collected data to the Prios API
   ========================================================================== */

/* --- Third-party libraries (loaded on demand) ------------------------------ */
// Served from jsDelivr: the bundled CSS resolves its flag sprite via the
// sibling ../img/ folder on the same CDN, so flags render correctly.
const ITI_VERSION = '18.2.1';
const ITI_CSS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/css/intlTelInput.min.css`;
const ITI_JS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/intlTelInput.min.js`;
const ITI_UTILS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/utils.js`;

// Max length the Prios API accepts for tel_mobile, counted on the full
// E.164 number (prefix included).
const TEL_MAX_LENGTH = 20;

function loadStyle(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', reject);
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    });
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

function loadIntlTelInput() {
  if (window.intlTelInput && window.intlTelInputUtils) return Promise.resolve();
  if (window.__priosItiPromise) return window.__priosItiPromise;
  loadStyle(ITI_CSS);
  // Load utils.js too so isValidNumber() and the example-number placeholder
  // (used to derive the per-country digit limit) are available at init time.
  window.__priosItiPromise = loadScript(ITI_JS).then(() => loadScript(ITI_UTILS));
  return window.__priosItiPromise;
}

function loadGoogleMaps(key) {
  if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve();
  if (window.__priosGmapPromise) return window.__priosGmapPromise;

  window.__priosGmapPromise = new Promise((resolve, reject) => {
    window.__priosGmapInit = () => resolve();
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&libraries=places&language=fr&callback=__priosGmapInit`;
    script.async = true;
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
  return window.__priosGmapPromise;
}

if (!customElements.get('customer-api-form')) {
  customElements.define(
    'customer-api-form',
    class CustomerApiForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.submitButtonText = this.querySelector('.customer-api-form__button-text');
        this.loadingSpinner = this.querySelector('.customer-api-form__loading');
        this.successMessageWrapper = this.querySelector('.customer-api-form__success-message-wrapper');
        this.errorMessageWrapper = this.querySelector('.customer-api-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.customer-api-form__error-message');

        this.telInput = this.querySelector('input[type="tel"]');
        this.iti = null;

        if (this.form) {
          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        this.initInputFilters();
        this.initPhoneInput();
        this.initAddressAutocomplete();
      }

      /* --- Field formatting / validation ------------------------------------ */

      // Strip digits from name / city fields as the user types.
      initInputFilters() {
        ['prenom', 'nom', 'ville'].forEach((name) => {
          const input = this.querySelector(`[name="${name}"]`);
          if (!input) return;
          input.addEventListener('input', () => {
            const cleaned = input.value.replace(/[0-9]/g, '');
            if (cleaned !== input.value) {
              const pos = input.selectionStart - 1;
              input.value = cleaned;
              input.setSelectionRange(pos, pos);
            }
          });
        });
      }

      initPhoneInput() {
        if (!this.telInput) return;

        this.maxNationalDigits = null;
        this.phoneErrorText = this.dataset.phoneErrorText || '';

        this.telInput.addEventListener('input', () => this.sanitizePhone());
        this.telInput.addEventListener('blur', () => this.validatePhone());

        loadIntlTelInput()
          .then(() => {
            this.iti = window.intlTelInput(this.telInput, {
              initialCountry: 'fr',
              preferredCountries: ['fr', 'be', 'ch', 'lu', 'gb'],
              separateDialCode: true,
              autoPlaceholder: 'aggressive',
            });
            const field = this.telInput.closest('.field--tel');
            if (field) field.classList.add('iti-ready');

            this.updateTelConstraints();
            this.telInput.addEventListener('countrychange', () => {
              this.updateTelConstraints();
              this.sanitizePhone();
              this.validatePhone();
            });
          })
          .catch((error) => {
            console.error('CustomerApiForm: failed to load intl-tel-input', error);
          });
      }

      // Remove letters and enforce the per-country digit limit while typing.
      sanitizePhone() {
        if (!this.telInput) return;
        let cleaned = this.telInput.value.replace(/[^0-9+\s().-]/g, '');
        if (this.maxNationalDigits) {
          let count = 0;
          cleaned = cleaned.replace(/\d/g, (digit) => (++count > this.maxNationalDigits ? '' : digit));
        }
        if (cleaned !== this.telInput.value) this.telInput.value = cleaned;
      }

      // Derive constraints from the country selected in intl-tel-input:
      //  - maxNationalDigits: expected number of digits (from the example
      //    number exposed as the placeholder by autoPlaceholder), e.g. FR = 10.
      //  - maxlength: char-level safety cap so the full E.164 number stays
      //    within the API's 20-char limit (20 minus the "+" + dial-code prefix).
      updateTelConstraints() {
        if (!this.iti || !this.telInput) return;
        const country = this.iti.getSelectedCountryData();

        const placeholder = this.telInput.getAttribute('placeholder') || '';
        const exampleDigits = (placeholder.match(/\d/g) || []).length;
        this.maxNationalDigits = exampleDigits > 0 ? exampleDigits : null;

        const prefixLength = country && country.dialCode ? country.dialCode.length + 1 : 0; // +1 for "+"
        const max = Math.max(1, TEL_MAX_LENGTH - prefixLength);
        this.telInput.setAttribute('maxlength', String(max));
      }

      // Flag the field invalid (native validation) when a number is entered
      // but isn't valid for the selected country.
      validatePhone() {
        if (!this.telInput) return;
        if (!this.iti || typeof window.intlTelInputUtils === 'undefined') {
          this.telInput.setCustomValidity('');
          return;
        }
        const hasValue = this.telInput.value.trim() !== '';
        if (hasValue && !this.iti.isValidNumber()) {
          this.telInput.setCustomValidity(this.phoneErrorText || 'Invalid phone number');
        } else {
          this.telInput.setCustomValidity('');
        }
      }

      /* --- Google Places address autocomplete ------------------------------- */

      initAddressAutocomplete() {
        const adresse = this.querySelector('[name="adresse"]');
        const gmapKey = this.dataset.gmapKey;
        if (!adresse || !gmapKey) return;

        loadGoogleMaps(gmapKey)
          .then(() => {
            // Prevent the form from submitting when picking a suggestion with Enter.
            adresse.addEventListener('keydown', (event) => {
              if (event.key === 'Enter') event.preventDefault();
            });

            const autocomplete = new google.maps.places.Autocomplete(adresse, {
              types: ['address'],
              fields: ['address_components'],
            });

            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              if (place && place.address_components) {
                this.fillAddressFromPlace(place.address_components);
              }
            });
          })
          .catch((error) => {
            console.error('CustomerApiForm: failed to load Google Maps', error);
          });
      }

      fillAddressFromPlace(components) {
        const get = (type) => components.find((c) => c.types.includes(type));

        const streetNumber = get('street_number')?.long_name || '';
        const route = get('route')?.long_name || '';
        const postalCode = get('postal_code')?.long_name || '';
        const city = get('locality')?.long_name || get('postal_town')?.long_name || '';
        const country = get('country');

        // The adresse field is bound to Google Autocomplete: dispatching an
        // "input" event on it would be read as a new keystroke and re-open the
        // predictions dropdown, so fill it silently.
        this.setFieldValue('adresse', `${streetNumber} ${route}`.trim(), { silent: true });
        this.setFieldValue('code_postal', postalCode);
        this.setFieldValue('ville', city);
        this.setFieldValue('pays', country ? country.long_name : '');
        this.setFieldValue('pays_iso', country ? country.short_name : '');
      }

      setFieldValue(name, value, { silent = false } = {}) {
        const input = this.querySelector(`[name="${name}"]`);
        if (!input) return;
        input.value = value;
        // The floating label is driven by :placeholder-shown (pure CSS) so it
        // updates from the value alone; the event is only for other listeners.
        if (!silent) input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      /* --- Submission -------------------------------------------------------- */

      async onSubmit(event) {
        event.preventDefault();

        // Refresh the phone field's custom validity, then enforce native +
        // custom validation (required, email format, no digits, valid phone…)
        this.validatePhone();
        if (this.form && !this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        const apiUrl = this.dataset.apiUrl;
        const apiKey = this.dataset.apiKey;

        if (!apiUrl || !apiKey) {
          console.error('CustomerApiForm: API URL or API Key is missing from data attributes.');
          return;
        }

        this.setLoadingState(true);
        this.clearMessages();

        // Use the international (E.164) phone number when intl-tel-input is ready.
        const telMobile = this.iti ? this.iti.getNumber() : this.getFieldValue('tel_mobile');

        // Retrieve field values
        const payload = {
          civilite: this.getFieldValue('civilite') || null,
          nom: this.getFieldValue('nom'),
          prenom: this.getFieldValue('prenom'),
          email: this.getFieldValue('email'),
          optin_email: this.getFieldChecked('optin_email'),
          tel_mobile: telMobile || null,
          optin_mobile: this.getFieldChecked('optin_mobile'),
          date_naissance: this.getFieldValue('date_naissance') || null,
          adresse: this.getFieldValue('adresse') || null,
          code_postal: this.getFieldValue('code_postal') || null,
          ville: this.getFieldValue('ville') || null,
          pays: this.getFieldValue('pays') || null,
          pays_iso: this.getFieldValue('pays_iso') || null,
        };

        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Api-Key': apiKey,
            },
            body: JSON.stringify(payload),
          });

          // The API returns an error HTTP status (e.g. 422) on validation
          // failures, with the details in the JSON body. So we parse the body
          // regardless of the status code rather than throwing on !response.ok.
          let result = null;
          try {
            result = await response.json();
          } catch (parseError) {
            result = null;
          }

          if (result && result.success) {
            this.handleSuccess();
          } else if (result) {
            // Surface the API's detailed validation errors. Front-end
            // validation should catch everything, but this is the safety net.
            const errorMsg = result.message || this.dataset.defaultErrorText;
            this.handleError(errorMsg, this.extractErrors(result));
          } else {
            // No parsable body (network/proxy error, empty response, …)
            this.handleError(this.dataset.defaultErrorText);
          }
        } catch (error) {
          console.error('CustomerApiForm Submission Error:', error);
          this.handleError(this.dataset.defaultErrorText);
        } finally {
          this.setLoadingState(false);
        }
      }

      getFieldValue(name) {
        const input = this.querySelector(`[name="${name}"]`);
        return input ? input.value.trim() : '';
      }

      getFieldChecked(name) {
        const input = this.querySelector(`[name="${name}"]`);
        return input ? input.checked : false;
      }

      setLoadingState(isLoading) {
        if (!this.form || !this.submitButton) return;

        // Disable all inputs in form to prevent edits during submission
        const inputs = this.form.querySelectorAll('input, select, button');
        inputs.forEach((input) => {
          input.disabled = isLoading;
        });

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');
          if (this.submitButtonText) {
            this.submitButtonText.innerText = this.submitButtonText.dataset.loadingText || 'Sending...';
          }
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
          if (this.submitButtonText) {
            this.submitButtonText.innerText = this.submitButtonText.dataset.defaultText || 'Submit';
          }
        }
      }

      clearMessages() {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.classList.add('hidden');
          this.errorMessageWrapper.setAttribute('hidden', '');
        }
        if (this.errorMessage) {
          this.errorMessage.innerText = '';
        }
      }

      handleSuccess() {
        if (this.form) {
          this.form.classList.add('hidden');
        }
        if (this.successMessageWrapper) {
          this.successMessageWrapper.classList.remove('hidden');
          this.successMessageWrapper.removeAttribute('hidden');
          this.successMessageWrapper.focus();
        }
      }

      // Flatten the API's { errors: { field: [messages] } } structure into a
      // single list of messages.
      extractErrors(result) {
        if (!result || !result.errors || typeof result.errors !== 'object') return [];
        return Object.values(result.errors).flat().filter(Boolean);
      }

      handleError(message, errors = []) {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.classList.remove('hidden');
          this.errorMessageWrapper.removeAttribute('hidden');
          this.errorMessageWrapper.focus();
        }
        if (!this.errorMessage) return;

        this.errorMessage.textContent = '';

        const summary = document.createElement('span');
        summary.textContent = message || this.dataset.defaultErrorText;
        this.errorMessage.appendChild(summary);

        if (errors.length) {
          const list = document.createElement('ul');
          list.className = 'customer-api-form__error-list';
          errors.forEach((detail) => {
            const item = document.createElement('li');
            item.textContent = detail;
            list.appendChild(item);
          });
          this.errorMessage.appendChild(list);
        }
      }
    }
  );
}
